/** O que o sino precisa fora do componente: o link de cada aviso e o
 *  "já vi" que outras telas mandam.
 *
 *  O link é montado AQUI, e não gravado no servidor, porque as rotas são
 *  do front: o backend diz o assunto (`kind` + `subject_id` +
 *  `company_id`) e quem conhece o mapa de telas decide pra onde ir. */

import { apiClient, type AppNotification, type AppNotificationKind } from "@/shared/api/api-client";

/** Evento de janela: "os avisos mudaram, releia". Quem marca um aviso
 *  como lido fora do sino dispara; o sino escuta e recarrega na hora, em
 *  vez de mostrar um aviso já visto até o próximo tick. */
export const NOTIFICATIONS_CHANGED_EVENT = "reputaition:notifications-changed";

export function notifyNotificationsChanged(): void {
  window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
}

/** Marca como visto o aviso de um assunto que a tela acabou de mostrar.
 *  Nunca lança: o sino é acessório, e a tela que chama já tem o que
 *  importa na mão. */
export async function markNoticeSeen(
  token: string,
  kind: AppNotificationKind,
  subjectId: string
): Promise<void> {
  try {
    await apiClient.markNotificationSubjectRead(token, kind, subjectId);
    notifyNotificationsChanged();
  } catch {
    // ver o docstring
  }
}

/** Pra onde o clique leva. `null` = aviso sem tela própria (mudança de
 *  configuração global, por exemplo) — o clique só marca como lido. */
export function notificationLink(notification: AppNotification): string | null {
  // Crédito ou cota de IA esgotado (só pro Máster): a tela onde se troca
  // a chave. Vem antes da empresa porque o aviso é da instalação.
  if (notification.kind === "ai_quota_exhausted") return "/admin/modelos-ia";
  const company = notification.company_id;
  if (!company) return null;
  const base = `/empresas/${encodeURIComponent(company)}`;
  switch (notification.kind) {
    case "upload":
    case "correction_batch":
      return notification.subject_id
        ? `${base}/uploads/${encodeURIComponent(notification.subject_id)}`
        : `${base}/uploads`;
    case "social_report":
      return `${base}/relatorios/cena`;
    case "catalog_import":
      return `${base}/configuracoes`;
    case "bulk_edit":
      return `${base}/mencoes`;
    // A execução vai no link: a tela de menções abre a faixa com o
    // resultado dela (alteradas, puladas, erros) — sem isso, quem chega
    // pelo sino depois do fim não via nada.
    case "ai_reprocess":
    case "company_data_reprocessed":
      return notification.subject_id
        ? `${base}/mencoes?reprocessamento=${encodeURIComponent(notification.subject_id)}`
        : `${base}/mencoes`;
    // A prévia reabre no diálogo, com o antes/depois e o mesmo recorte
    // (decisão do negócio, 2026-10-07).
    case "ai_reprocess_sample":
      return notification.subject_id
        ? `${base}/mencoes?previa=${encodeURIComponent(notification.subject_id)}`
        : `${base}/mencoes`;
    // A página de downloads (commit seguinte da exportação em fila)
    // abre com o item em destaque.
    case "export":
      return notification.subject_id
        ? `${base}/downloads?export=${encodeURIComponent(notification.subject_id)}`
        : `${base}/downloads`;
    case "config_change":
      return null;
    // Versa (ponto de encaixe 15): abre o relatório Versa cuja base ficou
    // pronta (ou falhou).
    case "versa_report":
      return notification.subject_id
        ? `${base}/versa/${encodeURIComponent(notification.subject_id)}`
        : `${base}/versa`;
    // A consultoria de regras com IA: abre a página de Justificativas com
    // a consultoria pedida reaberta.
    case "rule_review":
      return notification.subject_id
        ? `${base}/justificativas?consultoria=${encodeURIComponent(notification.subject_id)}`
        : `${base}/justificativas`;
  }
}

/** "agora", "há 5 min", "há 3 h", "ontem", "há 4 dias" — e a data, além
 *  de uma semana. O sino mostra 30 dias, então a data cobre o resto. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const seconds = Math.max(0, Math.round((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return "agora";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "ontem";
  if (days < 7) return `há ${days} dias`;
  return then.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}
